import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';

/**
 * Opened from the Job Apply Assistant Chrome extension (?extId=...).
 * Sends Firebase tokens to the extension via chrome.runtime.sendMessage (externally_connectable).
 */
export default function JobApplyExtensionConnect() {
  const [searchParams] = useSearchParams();
  const extId = searchParams.get('extId') || '';
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const connectSentRef = useRef(false);

  useEffect(() => {
    if (!extId) {
      setStatus('error');
      setMessage(
        'Missing extension id. Open this page using Sign in from the Job Apply Assistant extension.'
      );
      return undefined;
    }

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        connectSentRef.current = false;
        setStatus('needLogin');
        setMessage('');
        return;
      }
      if (connectSentRef.current) {
        return;
      }
      connectSentRef.current = true;
      try {
        const idToken = await user.getIdToken(true);
        const refreshToken = user.refreshToken;
        const firebaseWebApiKey = process.env.REACT_APP_FIREBASE_API_KEY || '';
        const extRuntime = window.chrome?.runtime;
        if (!extRuntime?.sendMessage) {
          setStatus('error');
          setMessage(
            'Chrome extension API is not available. Use Google Chrome with the Job Apply Assistant extension installed.'
          );
          return;
        }
        extRuntime.sendMessage(
          extId,
          {
            type: 'JAA_SAVE_AUTH',
            idToken,
            refreshToken: refreshToken || '',
            webAppUrl: window.location.origin,
            firebaseWebApiKey,
          },
          (response) => {
            const err = extRuntime.lastError;
            if (err) {
              connectSentRef.current = false;
              setStatus('error');
              setMessage(
                err.message ||
                  'Could not reach the extension. Confirm it is installed, enabled, and matches this site (see extension details).'
              );
              return;
            }
            if (response && response.ok) {
              setStatus('ok');
              setMessage(
                'Connected. You can close this tab and use Generate tailored resume in the extension.'
              );
            } else {
              connectSentRef.current = false;
              setStatus('error');
              setMessage((response && response.message) || 'Extension did not confirm.');
            }
          }
        );
      } catch (e) {
        connectSentRef.current = false;
        setStatus('error');
        setMessage(e.message || 'Failed to get credentials.');
      }
    });
    return () => unsub();
  }, [extId]);

  const returnPath =
    extId !== ''
      ? `/JobApplyExtensionConnect?extId=${encodeURIComponent(extId)}`
      : '/JobApplyExtensionConnect';

  return (
    <div
      style={{
        maxWidth: 520,
        margin: '48px auto',
        padding: 24,
        fontFamily: 'system-ui, Segoe UI, sans-serif',
      }}
    >
      <h1 style={{ marginTop: 0 }}>Job Apply Assistant</h1>
      {status === 'needLogin' && (
        <>
          <p>Sign in to Ops Management to connect the extension.</p>
          <p>
            <a href={`/Login?returnUrl=${encodeURIComponent(returnPath)}`}>Sign in</a>
          </p>
        </>
      )}
      {status === 'loading' && <p>Checking session…</p>}
      {status === 'ok' && <p style={{ color: '#1b5e20' }}>{message}</p>}
      {status === 'error' && <p style={{ color: '#b00020' }}>{message}</p>}
    </div>
  );
}
